import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box, Button, Chip, Dialog, DialogContent, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { AddCircleOutline, CheckCircle, Close as CloseIcon, Pause, PlayArrow, Place } from '@mui/icons-material';
import StopMotionImage from 'components/play/StopMotionImage';
import { muscleLabel, locationLabel, exerciseTypeLabel } from 'components/play/constants';
import { premiumButtonSx } from 'common/theme/button/premiumButtonSx';

/**
 * OTRIX Play — detalle de ejercicio ("Ver técnica"). Dos columnas en desktop: la animación
 * stop-motion grande (con play/pausa) a la izquierda y la ficha a la derecha (tipo, músculo
 * principal + secundarios, ubicación, series/reps y la técnica en pasos). Se abre desde la
 * tarjeta del catálogo y desde las filas de Mi rutina; es una superficie de consulta y alta,
 * no de registro de resultados.
 */

// La técnica suele venir como un bloque de texto. Si trae saltos de línea la mostramos como
// pasos numerados; si es un párrafo suelto, se respeta tal cual.
const parseSteps = (text) => {
  if (!text) return [];
  const lines = text
    .split('\n')
    .map((l) => l.trim())
    // Quita viñetas o numeraciones que ya traiga el texto para no duplicarlas.
    .map((l) => l.replace(/^(\d+[.)]\s*|[-•*]\s*)/, ''))
    .filter(Boolean);
  return lines.length > 1 ? lines : [];
};

function PlayExerciseDetailModal({
  exercise = null,
  open,
  onClose,
  onAdd = () => {},
  alreadyAdded = false
}) {
  const { t } = useTranslation(['play']);
  const [playing, setPlaying] = useState(true);

  const instructions = exercise?.localized_instructions || exercise?.localized_description || '';
  const steps = useMemo(() => parseSteps(instructions), [instructions]);
  const hasBothFrames = Boolean(exercise?.image_url_1) && Boolean(exercise?.image_url_2);

  if (!exercise) return null;

  const setsReps = [
    exercise.suggested_sets ? `${exercise.suggested_sets} ${t('card.series', { ns: 'play' })}` : null,
    exercise.suggested_reps ? `${exercise.suggested_reps} ${t('card.reps', { ns: 'play' })}` : null
  ]
    .filter(Boolean)
    .join(' · ');

  const secondary = (exercise.secondary_muscles || []).filter(Boolean);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: '16px', bgcolor: 'surface.content', backgroundImage: 'none' } }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'hairline.main' }}>
        <Typography variant="subtitle1" fontWeight={800} noWrap>
          {exercise.localized_name}
        </Typography>
        <IconButton onClick={onClose} aria-label={t('onboarding.close', { ns: 'play', defaultValue: 'Cerrar' })}>
          <CloseIcon />
        </IconButton>
      </Stack>

      <DialogContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 2, md: 3 }} alignItems="flex-start">
          {/* Animación */}
          <Box sx={{ width: '100%', flex: { md: '1 1 50%' }, minWidth: 0 }}>
            <Box sx={{ position: 'relative', borderRadius: '14px', overflow: 'hidden', border: '1px solid', borderColor: 'hairline.main' }}>
              <StopMotionImage
                frame1={exercise.image_url_1}
                frame2={exercise.image_url_2}
                alt={exercise.localized_name}
                height={360}
                playing={playing}
              />
              {hasBothFrames && (
                <Tooltip title={playing ? t('detail.pause', { ns: 'play' }) : t('detail.play', { ns: 'play' })}>
                  <IconButton
                    onClick={() => setPlaying((p) => !p)}
                    aria-label={playing ? t('detail.pause', { ns: 'play' }) : t('detail.play', { ns: 'play' })}
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      right: 8,
                      bgcolor: 'surface.overlay',
                      border: '1px solid',
                      borderColor: 'hairline.strong',
                      backdropFilter: 'blur(6px)',
                      color: 'gold.main',
                      transition: 'transform 0.2s cubic-bezier(0.32,0.72,0,1)',
                      '&:hover': { bgcolor: 'surface.hover' },
                      '&:active': { transform: 'scale(0.98)' }
                    }}>
                    {playing ? <Pause fontSize="small" /> : <PlayArrow fontSize="small" />}
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            {hasBothFrames && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75, textAlign: 'center' }}>
                {t('detail.framesHint', { ns: 'play' })}
              </Typography>
            )}
          </Box>

          {/* Ficha */}
          <Box sx={{ width: '100%', flex: { md: '1 1 50%' }, minWidth: 0 }}>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {exercise.exercise_type && (
                <Chip size="small" variant="outlined" label={exerciseTypeLabel(t, exercise.exercise_type)} />
              )}
              {exercise.muscle_group && (
                <Chip size="small" label={muscleLabel(t, exercise.muscle_group)} sx={{ bgcolor: 'gold.soft', color: 'gold.main' }} />
              )}
              {exercise.location && (
                <Chip size="small" variant="outlined" icon={<Place sx={{ fontSize: 16 }} />} label={locationLabel(t, exercise.location)} />
              )}
              {setsReps && <Chip size="small" variant="outlined" label={setsReps} />}
            </Stack>

            {secondary.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography
                  variant="caption"
                  fontWeight={800}
                  sx={{ color: 'text.secondary', letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', mb: 0.75 }}>
                  {t('detail.secondary', { ns: 'play' })}
                </Typography>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  {secondary.map((m) => (
                    <Chip key={m} size="small" variant="outlined" label={muscleLabel(t, m)} sx={{ height: 22 }} />
                  ))}
                </Stack>
              </Box>
            )}

            <Box sx={{ mt: 2 }}>
              <Typography
                variant="caption"
                fontWeight={800}
                sx={{ color: 'text.secondary', letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', mb: 0.75 }}>
                {t('detail.technique', { ns: 'play' })}
              </Typography>

              {steps.length > 0 ? (
                <Stack spacing={1}>
                  {steps.map((step, idx) => (
                    <Stack key={step} direction="row" spacing={1.25} alignItems="flex-start">
                      <Box
                        sx={{
                          flex: 'none',
                          width: 20,
                          height: 20,
                          mt: '2px',
                          borderRadius: '50%',
                          bgcolor: 'gold.soft',
                          color: 'gold.main',
                          fontSize: '0.7rem',
                          fontWeight: 800,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontVariantNumeric: 'tabular-nums'
                        }}>
                        {idx + 1}
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {step}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                  {instructions || t('card.noInstructions', { ns: 'play' })}
                </Typography>
              )}
            </Box>

            {alreadyAdded ? (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="center"
                sx={{ mt: 2.5, py: 1.25, borderRadius: 2, bgcolor: 'gold.soft', border: '1px solid', borderColor: 'hairline.main' }}>
                <CheckCircle sx={{ fontSize: 18, color: 'gold.main' }} />
                <Typography variant="body2" fontWeight={700} sx={{ color: 'gold.main' }}>
                  {t('detail.inRoutine', { ns: 'play' })}
                </Typography>
              </Stack>
            ) : (
              <Button
                fullWidth
                startIcon={<AddCircleOutline />}
                sx={{ ...premiumButtonSx, mt: 2.5 }}
                onClick={() => {
                  onAdd(exercise);
                  onClose();
                }}>
                {t('detail.add', { ns: 'play' })}
              </Button>
            )}
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

PlayExerciseDetailModal.propTypes = {
  exercise: PropTypes.shape({
    id: PropTypes.string,
    localized_name: PropTypes.string,
    localized_description: PropTypes.string,
    localized_instructions: PropTypes.string,
    muscle_group: PropTypes.string,
    secondary_muscles: PropTypes.arrayOf(PropTypes.string),
    location: PropTypes.string,
    exercise_type: PropTypes.string,
    suggested_sets: PropTypes.string,
    suggested_reps: PropTypes.string,
    image_url_1: PropTypes.string,
    image_url_2: PropTypes.string
  }),
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  /** Opcional: sin `onAdd` (o con alreadyAdded) el modal es solo lectura de la técnica. */
  onAdd: PropTypes.func,
  /** El ejercicio ya está en el día en curso: se reemplaza el CTA por una confirmación. */
  alreadyAdded: PropTypes.bool
};

export default PlayExerciseDetailModal;
